import StatusChip from 'components/@extended/StatusChip';
import { draftPageOf } from 'components/create-dialog/recordTypes';
import {
    deletesSoon,
    draftTypeName,
    MissingLine,
    retentionDaysOf,
    shareNames,
    sharerOf,
} from 'components/create-dialog/SavedForLaterList';
import EmptyState from 'components/EmptyState';
import { AccessTimeOutlined } from 'components/icons';
import MainCard from 'components/MainCard';
import { FormattedDate } from 'components/misc/FormattedDate';
import TabsGallery from 'components/TabsGallery';
import LinkWithOrigin from 'components/util/LinkWithOrigin';
import useAuth from 'hooks/useAuth';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetDraftsQuery } from 'store/api/drafts';
import { t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';

/** The rows of the card's one tab, or what stands in for them. */
function SavedForLaterRows() {
    const { user } = useAuth();
    const lastNameFirst = useLastNameFirst();
    const { data: drafts, isError, refetch } = useGetDraftsQuery();

    if (!drafts) {
        return isError ? <EmptyState size="panel" variant="error" onRetry={refetch} className="py-4" /> : null;
    }
    if (drafts.length === 0) {
        return (
            <EmptyState size="panel" icon={AccessTimeOutlined} titleId="create-dialog.saved.empty" className="py-4" />
        );
    }
    return (
        <>
            <p className="px-5 pt-3 pb-1">
                <FormattedMessage
                    id="create-dialog.saved.summary"
                    values={{
                        count: drafts.length,
                        days: retentionDaysOf(drafts),
                        b: (chunks: ReactNode) => (
                            <strong key="b" className="font-semibold">
                                {chunks}
                            </strong>
                        ),
                    }}
                />
            </p>
            <ul>
                {drafts.map((entry) => {
                    const page = draftPageOf(entry.type, entry.id);
                    const typeName = draftTypeName(entry.type);
                    const sharer = sharerOf(entry, user?.id, lastNameFirst);
                    const share = sharer
                        ? t('create-dialog.saved.by', { name: sharer })
                        : entry.shares.length > 0
                          ? t('create-dialog.saved.with', { names: shareNames(entry, lastNameFirst) })
                          : null;
                    return (
                        <li
                            key={`${entry.type}/${entry.id}`}
                            className="border-grey-200 flex items-center gap-3 border-t px-5 py-3.5"
                        >
                            <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
                                <LinkWithOrigin to={page} className="text-section text-fg no-underline">
                                    {entry.name ?? typeName}
                                </LinkWithOrigin>
                                <span className="text-grey-600">
                                    {typeName} · <MissingLine entry={entry} />
                                    {share ? ` · ${share}` : null}
                                </span>
                                {deletesSoon(entry) ? (
                                    <StatusChip
                                        type="warning"
                                        className="text-warning-darker mt-0.5"
                                        label={
                                            <FormattedMessage
                                                id="create-dialog.saved.deleted-on"
                                                values={{ date: <FormattedDate key="date" value={entry.deletesOn} /> }}
                                            />
                                        }
                                    />
                                ) : null}
                            </div>
                            <LinkWithOrigin
                                to={page}
                                className="border-divider bg-grey-50 hover:bg-grey-100 text-meta text-fg flex h-8 items-center rounded-md border px-2.5 font-semibold no-underline"
                            >
                                <FormattedMessage id="create-dialog.saved.open" />
                            </LinkWithOrigin>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}

/** The classic dashboard's Saved for later: every draft the user sees, latest save first, each opening its page. */
export default function SavedForLaterCard() {
    const { data: drafts } = useGetDraftsQuery();
    const count = drafts?.length ?? 0;
    return (
        <MainCard contentClassName="px-0 pt-0">
            <TabsGallery
                className="mb-0"
                tabs={[
                    {
                        id: 'saved-for-later',
                        label: t('create-dialog.saved.title'),
                        icon: <StatusChip size="small" label={count} type="neutral" />,
                        component: <SavedForLaterRows />,
                    },
                ]}
            />
        </MainCard>
    );
}
